import React, { useEffect } from 'react'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'

import useInvites from '@/modules/client/_helpers/useInvites'
import { SubTitle, Title } from '@/modules/base/components/texts'
import ThemeInterface from '@/theme-interface'
import ClientInvitationItem from '@/modules/dashboard/view/dashboard-invitations/client-invitation-item'
import { useAppSelector } from '@/redux/store'
import lang from '@/lang'
import { selectInvitingClients } from './select-inviting-clients'

interface DashboardClientInvitationsProps {
  /** Rendered inside a widget card, which already provides the frame and the heading. */
  embedded?: boolean
  /** Closes the modal this is shown in; called once no invitation is left. */
  onModalClose?: () => void
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  container: {
    padding: 30,
    background: theme.white,
  },
  invitations: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 20,
    gap: 20,
    maxHeight: 800,
    overflowY: 'auto',
  },
  invitationsEmbedded: {
    marginTop: 12,
  },
}))

type InvitationListProps = {
  invitingClients: { id: number | string }[]
  embedded: boolean
}

const InvitationList = ({ invitingClients, embedded }: InvitationListProps) => {
  const classes = useStyles()
  const { subscriptions } = useAppSelector(state => state.tenant.info)
  const isTaxConsultant = subscriptions?.some(s => s.isTaxConsultant)

  return (
    <>
      <SubTitle>{lang.dashboard.widgetDetails.clientInvitations.subTitle}</SubTitle>
      <div className={clsx(classes.invitations, embedded && classes.invitationsEmbedded)}>
        {invitingClients.map(client => (
          <ClientInvitationItem key={client.id} client={client} isTaxConsultant={isTaxConsultant} />
        ))}
      </div>
    </>
  )
}

// Loaded and kept current elsewhere — see `selectInvitingClients` for where, and
// why the widget does not do it too.
const EmbeddedInvitations = () => {
  const invitingClients = useAppSelector(selectInvitingClients)

  // Only reachable in edit mode: outside it the dashboard leaves the widget off
  // while there is nothing pending, so this explains the empty card there.
  if (!invitingClients.length) {
    return (
      <div data-qs-id='dashboard-invitations-placeholder'>
        <SubTitle>{lang.dashboard.widgetDetails.clientInvitations.editPlaceholder}</SubTitle>
      </div>
    )
  }

  return <InvitationList invitingClients={invitingClients} embedded />
}

// The modal can open on any page, so it loads the invitations itself.
const ModalInvitations = ({ onModalClose }: Pick<DashboardClientInvitationsProps, 'onModalClose'>) => {
  const classes = useStyles()
  const { invitingClients } = useInvites(false, false)

  useEffect(() => {
    if (onModalClose && !invitingClients.length) onModalClose()
  }, [onModalClose, invitingClients.length])

  return (
    <div className={classes.container}>
      <Title marginBottom={'5px'}>{lang.dashboard.widgetDetails.clientInvitations.title}</Title>
      <InvitationList invitingClients={invitingClients} embedded={false} />
    </div>
  )
}

const DashboardClientInvitations = ({ embedded = false, onModalClose }: DashboardClientInvitationsProps) => (
  embedded ? <EmbeddedInvitations /> : <ModalInvitations onModalClose={onModalClose} />
)

export default DashboardClientInvitations
